import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CheckCircle2, AlertTriangle, User, Sparkles, X, Lightbulb } from "lucide-react";
import api from "@/lib/api";

/**
 * Dialog di conferma post-OCR. Mostra ogni riga riconosciuta con:
 *  - dipendente rilevato (con badge certezza)
 *  - se incerto, dropdown candidati top-3 + opzione "nessuno"
 *  - ore + commessa per contesto
 *
 * Props:
 *  - open: bool
 *  - ocrData: il payload completo restituito da /api/rapportini/scan
 *  - employees: lista dipendenti del DB (per dropdown)
 *  - onConfirm(resolvedOcrData): conferma con i dipendenti scelti
 *  - onCancel(): annulla l'import OCR
 */
export default function OcrConfirmDialog({
  open,
  ocrData,
  employees = [],
  onConfirm,
  onCancel,
}) {
  // Flatten rows with day reference + carry resolved employee_id
  const initialRows = useMemo(() => {
    if (!ocrData) return [];
    const out = [];
    (ocrData.giorni || []).forEach((g, dIdx) => {
      (g.righe || []).forEach((r, rIdx) => {
        out.push({
          dIdx, rIdx,
          data: g.data || "",
          raw_name: r.employee_nome || "",
          employee_id: r.employee_id || "",
          commessa: r.commessa_codice || "",
          ore_lavoro: r.ore_lavoro || 0,
          ore_guida: r.ore_guida || 0,
          ore_passegg: r.ore_passegg || 0,
          ore_caldo: r.ore_caldo || 0,
          ore_notturne: r.ore_notturne || 0,
          ore_tot: r.ore_tot || 0,
        });
      });
    });
    return out;
  }, [ocrData]);

  const [resolved, setResolved] = useState(initialRows);
  // raw_name (lowercased) → true se l'utente vuole salvarlo come alias
  const [learnAliases, setLearnAliases] = useState({});

  // Get match metadata (certainty + candidates) by raw name
  const metaByRaw = useMemo(() => {
    const m = {};
    (ocrData?.matches?.employees || []).forEach((e) => {
      m[(e.raw || "").toLowerCase()] = e;
    });
    return m;
  }, [ocrData]);

  if (!ocrData) return null;

  const setEmployee = (idx, employee_id) => {
    setResolved((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, employee_id } : r))
    );
  };

  const handleConfirm = async () => {
    // Save aliases (best-effort) for rows where the user opted-in.
    // Only save for non-exact matches that now have a resolved employee_id.
    const aliasTasks = [];
    for (const row of resolved) {
      const meta = metaByRaw[(row.raw_name || "").toLowerCase()];
      const isExact = meta?.certainty === "exact";
      const wantLearn = learnAliases[(row.raw_name || "").toLowerCase()];
      if (!isExact && wantLearn && row.employee_id && row.raw_name) {
        aliasTasks.push(
          api.post(`/employees/${row.employee_id}/aliases`, { alias: row.raw_name })
            .catch((e) => console.warn("[ocr] alias save failed:", row.raw_name, e?.message))
        );
      }
    }
    if (aliasTasks.length) await Promise.all(aliasTasks);

    // Build updated giorni with resolved employee_id
    const newGiorni = (ocrData.giorni || []).map((g, dIdx) => ({
      ...g,
      righe: (g.righe || []).map((r, rIdx) => {
        const fix = resolved.find((x) => x.dIdx === dIdx && x.rIdx === rIdx);
        if (!fix) return r;
        const emp = employees.find((e) => e.id === fix.employee_id);
        return {
          ...r,
          employee_id: fix.employee_id || null,
          employee_nome: emp
            ? `${emp.cognome} ${emp.nome}`
            : r.employee_nome || fix.raw_name,
        };
      }),
    }));
    onConfirm({ ...ocrData, giorni: newGiorni });
  };

  const unresolved = resolved.filter((r) => !r.employee_id).length;
  const ambigui = resolved.filter((r) => {
    const meta = metaByRaw[(r.raw_name || "").toLowerCase()];
    return meta && meta.certainty !== "exact" && meta.certainty !== "high";
  }).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col"
        data-testid="ocr-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <Sparkles className="h-6 w-6 text-[#0044FF]" />
            Conferma il rapportino scansionato
          </DialogTitle>
          <DialogDescription>
            L'AI ha letto il rapportino. Controlla le ore e
            <b> assegna ogni nome a un dipendente esistente</b> prima di salvare.
            <span className="block mt-1 text-orange-700">
              I dipendenti nuovi vanno inseriti separatamente da
              "Anagrafica → Dipendenti".
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Header info */}
          <Card className="p-4 bg-slate-50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-500 uppercase font-bold">N°</div>
                <div className="font-heading font-black text-lg">
                  #{ocrData.numero || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase font-bold">Dal</div>
                <div className="font-bold">{ocrData.data_inizio || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase font-bold">Al</div>
                <div className="font-bold">{ocrData.data_fine || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase font-bold">Righe</div>
                <div className="font-bold">{resolved.length}</div>
              </div>
            </div>
          </Card>

          {/* Counters */}
          {(unresolved > 0 || ambigui > 0) && (
            <div className="flex flex-wrap gap-2">
              {ambigui > 0 && (
                <div
                  className="bg-orange-50 text-orange-800 border border-orange-200 rounded-md px-3 py-2 text-sm flex items-center gap-2"
                  data-testid="ocr-ambigui-count"
                >
                  <AlertTriangle className="h-4 w-4" />
                  {ambigui} {ambigui === 1 ? "nome incerto" : "nomi incerti"} —
                  scegli a quale dipendente abbinarli.
                </div>
              )}
            </div>
          )}

          {/* Rows */}
          {resolved.map((row, idx) => {
            const meta = metaByRaw[(row.raw_name || "").toLowerCase()];
            const certainty = meta?.certainty || "none";
            const candidates = meta?.candidates || [];
            const isExact = certainty === "exact" || certainty === "high";
            const hasEmp = !!row.employee_id;
            return (
              <Card
                key={`${row.dIdx}-${row.rIdx}`}
                className={`p-4 ${
                  !hasEmp && !isExact ? "border-orange-300 bg-orange-50/40" : ""
                }`}
                data-testid={`ocr-row-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`h-9 w-9 shrink-0 rounded-full grid place-items-center ${
                      hasEmp
                        ? "bg-green-100 text-green-700"
                        : "bg-orange-100 text-orange-700"
                    }`}
                  >
                    {hasEmp ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <User className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-base">
                        AI ha letto:
                      </span>
                      <span
                        className="font-mono bg-white border border-slate-200 rounded px-2 py-0.5 text-sm"
                        data-testid={`ocr-raw-name-${idx}`}
                      >
                        {row.raw_name || "(vuoto)"}
                      </span>
                      {certainty === "exact" && (
                        <span className="text-xs text-green-700 font-bold uppercase">
                          ✓ esatto
                        </span>
                      )}
                      {certainty === "high" && (
                        <span className="text-xs text-green-700 font-bold uppercase">
                          ✓ alta similarità
                        </span>
                      )}
                      {certainty === "low" && (
                        <span className="text-xs text-orange-700 font-bold uppercase">
                          ⚠ incerto
                        </span>
                      )}
                      {certainty === "none" && (
                        <span className="text-xs text-red-700 font-bold uppercase">
                          ✗ nessun match
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-slate-600 mt-1">
                      {row.data} · {row.commessa || "—"} ·
                      <span className="font-bold ml-1">
                        {row.ore_tot}h tot.
                      </span>{" "}
                      (lavoro {row.ore_lavoro}h / guida {row.ore_guida}h /
                      pass. {row.ore_passegg}h
                      {row.ore_caldo > 0 && <> / 🔥 {row.ore_caldo}h</>}
                      {row.ore_notturne > 0 && <> / 🌙 {row.ore_notturne}h</>})
                    </div>

                    <div className="mt-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
                        Abbina a dipendente
                      </div>
                      <Select
                        value={row.employee_id || "__none__"}
                        onValueChange={(v) =>
                          setEmployee(
                            idx,
                            v === "__none__" ? "" : v
                          )
                        }
                      >
                        <SelectTrigger
                          className="h-11"
                          data-testid={`ocr-select-${idx}`}
                        >
                          <SelectValue placeholder="Scegli il dipendente" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            — lascia vuoto —
                          </SelectItem>
                          {/* Top candidates first */}
                          {candidates.length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-bold text-slate-500 uppercase">
                                Più simili
                              </div>
                              {candidates.map((c) => (
                                <SelectItem
                                  key={`cand-${c.id}`}
                                  value={c.id}
                                  data-testid={`ocr-candidate-${idx}-${c.id}`}
                                >
                                  {c.cognome} {c.nome}
                                  {c.ruolo ? ` · ${c.ruolo}` : ""}
                                </SelectItem>
                              ))}
                              <div className="px-2 py-1 text-xs font-bold text-slate-500 uppercase border-t border-slate-100 mt-1">
                                Tutti i dipendenti
                              </div>
                            </>
                          )}
                          {employees
                            .filter(
                              (e) => !candidates.find((c) => c.id === e.id)
                            )
                            .map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.cognome} {e.nome}
                                {e.ruolo ? ` · ${e.ruolo}` : ""}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {/* Suggerimento alias — solo per match non-esatti con dipendente scelto */}
                      {!isExact && row.employee_id && row.raw_name && (
                        <label
                          className="mt-2 flex items-center gap-2 text-xs text-slate-700 cursor-pointer"
                          data-testid={`ocr-learn-alias-${idx}`}
                        >
                          <input
                            type="checkbox"
                            checked={
                              learnAliases[(row.raw_name || "").toLowerCase()] ?? true
                            }
                            onChange={(e) =>
                              setLearnAliases((prev) => ({
                                ...prev,
                                [(row.raw_name || "").toLowerCase()]: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 accent-[#0044FF]"
                          />
                          <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
                          <span>
                            Ricorda "<b>{row.raw_name}</b>" come alias di questo
                            dipendente per la prossima scansione
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <DialogFooter className="mt-3 flex flex-col-reverse md:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            className="h-12"
            data-testid="ocr-cancel-button"
          >
            <X className="h-4 w-4 mr-2" /> Annulla import
          </Button>
          <Button
            onClick={handleConfirm}
            className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
            data-testid="ocr-confirm-button"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Conferma e pre-compila ({resolved.length} righe)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
